/** Admin / Manage tabs for events: Events, Event Templates, Locations. */
import { api } from "../api";
import { S, isAdmin, on, render } from "../state";
import { $, I, closeSheet, edHead, esc, openSheet, sized, toast } from "../ui";
import { DURATIONS, EVENT_TYPES, fmtDay, fmtTime, typeInfo } from "../../shared/constants";
import type { EventView, LocationView, TemplateView } from "../../shared/types";
import { E, colorOf, put, shortTitle, sortEv } from "../events";
import { byName, locSel, openEditor, posSel, setDot, shChk, tget, tpick, typeSel } from "./editor";

/* ---------------- Events tab ---------------- */

const EF: { from: string; to: string; type: string; status: string; q: string; list: EventView[] | null; key: string } = {
  from: "", to: "", type: "", status: "", q: "", list: null, key: "",
};

export function resetManage() {
  EF.list = null;
  EF.key = "";
}

async function loadEvents() {
  const key = `${EF.from}|${EF.to}`;
  if (EF.key === key && EF.list) return;
  EF.key = key;
  try {
    const list = await api<EventView[]>(`/api/events?from=${EF.from || "2000-01-01"}&to=${EF.to || "9999-12-31"}&canceled=1`);
    if (EF.key !== key) return;
    EF.list = list;
    for (const e of list) if (E.byId.has(e.id) || e.status !== "canceled") E.byId.set(e.id, e);
  } catch (e) {
    toast((e as Error).message);
    EF.list = [];
  }
  const t = $("#evtable");
  if (t) t.innerHTML = evTable();
}

function evRows() {
  const q = EF.q.toLowerCase();
  return (EF.list || [])
    .map((e) => E.byId.get(e.id) || e)
    .filter((e) => (!EF.type || e.type === EF.type) && (!EF.status || e.status === EF.status) && (!q || e.title.toLowerCase().includes(q)))
    .sort(sortEv);
}

const stChip = (s: string) =>
  `<span class="chip ${s === "published" ? "blue" : s === "canceled" ? "red" : s === "completed" ? "ok" : "warn"}">${s[0].toUpperCase() + s.slice(1)}</span>`;
const names = (e: EventView, k: "PA" | "SA") =>
  e.signups
    .filter((s) => s.kind === k)
    .map((s) => esc(`${s.first} ${s.last}`.trim()) + (s.shadow ? " (Shadow)" : ""))
    .join(", ");

function evTable() {
  if (!EF.list) return '<p class="muted">Loading…</p>';
  const rows = evRows();
  return `<p class="muted small" style="margin:0 0 8px">${rows.length} event${rows.length === 1 ? "" : "s"}</p><div class="tablewrap"><table><thead><tr><th>When</th><th>Event</th><th>Status</th><th>Parent Ambassadors</th><th>Student Ambassadors</th><th></th></tr></thead><tbody>${rows
    .slice(0, 150)
    .map(
      (e) => `<tr>
  <td class="small" style="white-space:nowrap">${fmtDay(e.date)}<div class="muted">${fmtTime(e.start)}</div></td>
  <td><span class="dot" style="background:${colorOf(e)}"></span> <a href="#" data-act="open-event" data-id="${e.id}"><b>${esc(shortTitle(e.title))}</b></a></td>
  <td>${stChip(e.status)}</td><td class="small">${e.type === "team" ? "—" : names(e, "PA") || '<span class="muted">None</span>'}</td><td class="small">${e.type === "team" ? "—" : names(e, "SA") || '<span class="muted">None</span>'}</td>
  <td style="white-space:nowrap">${e.status === "canceled" ? "" : `<button class="btn ghost sm" data-act="mg-edit" data-id="${e.id}">Edit</button> `}<button class="btn ghost sm" data-act="mg-dup" data-id="${e.id}">Duplicate</button></td></tr>`,
    )
    .join("")}</tbody></table></div>${rows.length > 150 ? '<p class="muted small">Showing the first 150. Narrow the dates to see more.</p>' : ""}`;
}

export function eventsTab() {
  if (!EF.from) {
    EF.from = S.me!.today;
    EF.to = `${S.me!.schoolYear + 1}-06-30`;
  }
  void loadEvents();
  const drafts = (EF.list || []).filter((e) => (E.byId.get(e.id) || e).status === "draft").length;
  const types = byName([...EVENT_TYPES], (t) => t.label);
  return `<div class="card"><div class="row between wrap" style="gap:10px;margin-bottom:12px">
  <div class="row wrap" style="gap:8px;align-items:flex-end">
    <div><label class="fld" for="ef1">From</label><input type="date" id="ef1" value="${EF.from}" data-change="ef-from"></div>
    <div><label class="fld" for="ef2">To</label><input type="date" id="ef2" value="${EF.to}" data-change="ef-to"></div>
    <div><label class="fld" for="ef3">Type</label><select class="f" id="ef3" data-change="ef-type"><option value="">All Types</option>${types.map((t) => `<option value="${t.key}" ${EF.type === t.key ? "selected" : ""}>${t.label}</option>`).join("")}</select></div>
    <div><label class="fld" for="ef4">Status</label><select class="f" id="ef4" data-change="ef-status"><option value="">All Statuses</option>${["canceled", "completed", "draft", "published"].map((s) => `<option value="${s}" ${EF.status === s ? "selected" : ""}>${s[0].toUpperCase() + s.slice(1)}</option>`).join("")}</select></div>
    <div><label class="fld" for="ef5">Search</label><input type="text" id="ef5" value="${esc(EF.q)}" placeholder="Event name" data-input="ef-q"></div>
  </div>
  <div class="row wrap"><button class="btn sm" data-act="new-event">${sized(I.plus, 14)} Add</button><button class="btn ghost sm" data-act="ask-publish-drafts">Publish All Drafts${drafts ? ` (${drafts})` : ""}</button>${isAdmin() ? '<button class="btn ghost sm" data-act="export-events">Export CSV</button>' : ""}</div></div>
  <div id="evtable">${evTable()}</div>${isAdmin() ? '<p class="muted small" style="margin:10px 0 0">The CSV includes canceled events, each event\'s status, and one row per ambassador.</p>' : ""}</div>`;
}

const refreshTable = () => {
  const t = $("#evtable");
  if (t) t.innerHTML = evTable();
};

async function askPublishDrafts() {
  let ds: EventView[];
  try {
    ds = (await api<EventView[]>(`/api/events?from=2000-01-01&to=9999-12-31`)).filter((e) => e.status === "draft").sort(sortEv);
  } catch (e) {
    return toast((e as Error).message);
  }
  openSheet(`${edHead("Publish Draft Events")}<div class="edbody">
  ${
    ds.length
      ? `<section class="edsec"><h3>${ds.length} draft event${ds.length === 1 ? "" : "s"} will be published</h3><ul class="list small">${ds.map((e) => `<li><span class="dot" style="background:${colorOf(e)}"></span> <b>${esc(shortTitle(e.title))}</b> <span class="muted">${fmtDay(e.date)} at ${fmtTime(e.start)}</span></li>`).join("")}</ul></section>
  <section class="edsec"><h3>Notifications</h3><div class="edtpl"><label class="row" style="gap:10px;align-items:flex-start;cursor:pointer"><input type="checkbox" id="pdn" checked style="margin-top:3px"><span><b>Notify ambassadors</b><br><span class="muted small">Each ambassador gets one notification covering only the events that match their calendar filters and have an open spot for their role. It goes out right away, and includes any single events still waiting on their 10-minute timer.</span></span></label></div></section>`
      : '<p class="muted">There are no draft events right now.</p>'
  }</div>
  <div class="edfoot">${ds.length ? `<button class="btn" data-act="publish-drafts" data-n="${ds.length}">Publish ${ds.length} Event${ds.length === 1 ? "" : "s"}</button>` : ""}<button class="btn ghost" data-act="close-sheet">Cancel</button></div>`);
}

const csvCell = (v: unknown) => {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const INVITE_LABEL: Record<string, string> = { "": "", accepted: "Accepted", declined: "Declined", maybe: "Maybe", none: "Not Sent", opted_out: "Invites Off", sent: "No Response" };

function exportEvents() {
  const out: unknown[][] = [["Date", "Start", "End", "Event", "Type", "Status", "Location", "Ambassador", "Role", "Invite", "Confirmed", "Checked In"]];
  for (const e of evRows()) {
    const base = [e.date, fmtTime(e.start), fmtTime(e.end), e.title, typeInfo(e.type).label, e.status, e.locationName || ""];
    if (!e.signups.length) out.push([...base, "", "", "", "", ""]);
    for (const s of e.signups)
      out.push([...base, `${s.first} ${s.last}`.trim(), s.kind + (s.shadow ? " Shadow" : ""), INVITE_LABEL[s.invite || ""] ?? s.invite, s.confirmed ? "Yes" : "", s.checkedIn ? "Yes" : s.walkin ? "Walk-In" : ""]);
  }
  const csv = out.map((r) => r.map(csvCell).join(",")).join("\r\n") + "\r\n";
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = `events-${EF.from || "start"}-to-${EF.to || "end"}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

/* ---------------- Templates tab (admins) ---------------- */

const durLabel = (d: number) => (d < 60 ? `${d} min` : d % 60 ? `${Math.floor(d / 60)} hr ${d % 60} min` : `${d / 60} hr${d > 60 ? "s" : ""}`);
const locName = (id: number | null) => (id ? E.locations?.find((l) => l.id === id)?.name || "" : "");

export function templatesTab() {
  if (!E.templates || !E.locations) {
    void loadLists();
    return '<div class="card"><p class="muted">Loading…</p></div>';
  }
  const list = byName(E.templates, (t) => t.name);
  return `<div class="card">${
    list.length
      ? `<div class="tablewrap"><table><thead><tr><th>Template</th><th>Length</th><th>Spots</th><th>Location</th><th></th></tr></thead><tbody>${list
          .map(
            (t) => `<tr class="click" tabindex="0" data-act="open-tpl" data-id="${t.id}"><td><span class="dot" style="background:${typeInfo(t.type).color}"></span> <b>${esc(t.name)}</b></td><td>${durLabel(t.duration)}</td><td class="small">${t.type === "team" ? "No sign-up" : `${t.pa} PA${t.paShadow ? " + shadows" : ""} · ${t.sa} SA${t.saShadow ? " + shadows" : ""}`}</td><td class="small">${esc(locName(t.locationId))}</td><td><span class="btn ghost sm">Edit</span></td></tr>`,
          )
          .join("")}</tbody></table></div>`
      : '<p class="muted" style="margin:0">No templates yet. Templates fill in an event\'s name, type, time, location and spots in one step.</p>'
  }
  <button class="btn sm" style="margin-top:12px" data-act="open-tpl" data-id="0">${sized(I.plus, 14)} New Template</button></div>`;
}

async function loadLists() {
  try {
    E.locations = await api<LocationView[]>("/api/events/locations");
    E.templates = await api<TemplateView[]>("/api/events/templates");
  } catch (e) {
    toast((e as Error).message);
    E.locations ??= [];
    E.templates ??= [];
  }
  render();
}

function openTpl(id: number) {
  const t: TemplateView = E.templates?.find((x) => x.id === id) || { id: 0, name: "", type: "", duration: 60, start: "09:00", locationId: null, pa: 1, sa: 0, paShadow: false, saShadow: false };
  openSheet(`${edHead(id ? "Edit Template" : "New Template")}<div class="edbody">
    <section class="edsec"><h3>Template</h3>
      <div><label class="fld" for="tn">Name <span class="muted small" id="tnc">${t.name.length}/30</span></label><input type="text" id="tn" maxlength="30" value="${esc(t.name)}" data-input="tpl-count"></div>
      <div><label class="fld" for="tt">Type &amp; Color</label>${typeSel("tt", t.type, "tpl-type")}</div></section>
    <section class="edsec"><h3>When &amp; Where</h3>
      <div class="grid g2"><div><label class="fld">Usual Start</label>${tpick("tst", t.start, "")}</div><div><label class="fld" for="tdur">Length</label><select class="f" id="tdur">${DURATIONS.map((d) => `<option value="${d}" ${d === t.duration ? "selected" : ""}>${durLabel(d)}</option>`).join("")}</select></div></div>
      <div><label class="fld" for="tl">Location</label>${locSel("tl", t.locationId, locName(t.locationId))}</div></section>
    <section class="edsec"><h3>Ambassadors Needed</h3><div class="grid" style="grid-template-columns:1fr 1fr;gap:10px"><div>${posSel("tpa", t.pa, "Parent Ambassadors")}${shChk("tpsh", t.paShadow, "Allow PAs to Shadow")}</div><div>${posSel("tsa", t.sa, "Student Ambassadors")}${shChk("tssh", t.saShadow, "Allow SAs to Shadow")}</div></div></section>
    <div class="err" id="err" role="alert"></div>
  </div><div class="edfoot"><button class="btn" data-act="save-tpl" data-id="${id}">Save Template</button><button class="btn ghost" data-act="close-sheet">Cancel</button>${id ? `<span id="tdel" style="margin-left:auto"><button class="btn ghost" style="color:var(--accent)" data-act="ask-del-tpl" data-id="${id}">Delete</button></span>` : ""}</div>`);
}

const val = (id: string) => ($(`#${id}`) as HTMLInputElement).value;
const checked = (id: string) => ($(`#${id}`) as HTMLInputElement).checked;

/* ---------------- Locations tab (admins) ---------------- */

export function locationsTab() {
  if (!E.locations || !E.templates) {
    void loadLists();
    return '<div class="card"><p class="muted">Loading…</p></div>';
  }
  const list = byName(E.locations.filter((l) => !l.deleted), (l) => l.name);
  return `<div class="card"><p class="muted small" style="margin:0 0 10px">Events and templates pick from this list, so the location text is always identical. Tap a location to edit its details.</p>
  ${
    list.length
      ? `<ul class="list">${list.map((l) => `<li><button class="erow" style="border:0;padding:0;background:none;width:100%;text-align:left" data-act="open-loc" data-id="${l.id}"><div class="body"><b>${esc(l.name)}</b>${l.address ? `<div class="small">${esc(l.address)}</div>` : ""}${l.directions ? `<div class="muted small">${esc(l.directions)}</div>` : ""}</div></button></li>`).join("")}</ul>`
      : '<p class="muted">No locations yet.</p>'
  }
  <button class="btn sm" style="margin-top:12px" data-act="open-loc" data-id="0">${sized(I.plus, 14)} Add</button></div>`;
}

function openLoc(id: number) {
  const l = E.locations?.find((x) => x.id === id) || { id: 0, name: "", address: "", directions: "", deleted: false };
  openSheet(`${edHead(id ? "Edit Location" : "Add Location")}<div class="edbody">
  <section class="edsec"><h3>Location</h3>
    <div><label class="fld" for="lname">Name</label><input type="text" id="lname" maxlength="60" value="${esc(l.name)}" placeholder="e.g. North Gate parking"></div>
    <div><label class="fld" for="laddr">Address</label><input type="text" id="laddr" maxlength="200" value="${esc(l.address)}" placeholder="Street address"><div class="muted small" style="margin-top:4px">Sent as the Location in the calendar invite, so maps can find it.</div></div>
    <div><label class="fld" for="linfo">Directions for Ambassadors (optional)</label><div class="muted small" style="margin:-2px 0 6px">Added to the invite notes, under the event's own notes.</div><textarea id="linfo" maxlength="1000" placeholder="e.g. Park in the visitor spots and meet at the gate.">${esc(l.directions)}</textarea></div></section>
  ${id ? `<div class="edtpl small">Saving fixes this location everywhere it's used. <b>No updated invites are sent.</b> The changes show up in future invites and any invite that gets re-sent.</div>` : ""}
  <div class="err" id="err" role="alert"></div></div>
  <div class="edfoot"><button class="btn" data-act="save-loc" data-id="${id}">Save</button><button class="btn ghost" data-act="close-sheet">Cancel</button>${id ? `<span id="ldel" style="margin-left:auto"><button class="btn ghost" style="color:var(--accent)" data-act="ask-del-loc" data-id="${id}">Delete</button></span>` : ""}</div>`);
}

const showErr = (e: unknown) => {
  const m = (e as Error).message;
  const el = $("#err");
  if (el) el.textContent = m;
  toast(m);
};

on({
  "ef-from": (el) => {
    EF.from = (el as HTMLInputElement).value;
    EF.list = null;
    refreshTable();
    void loadEvents();
  },
  "ef-to": (el) => {
    EF.to = (el as HTMLInputElement).value;
    EF.list = null;
    refreshTable();
    void loadEvents();
  },
  "ef-type": (el) => {
    EF.type = (el as HTMLSelectElement).value;
    refreshTable();
  },
  "ef-status": (el) => {
    EF.status = (el as HTMLSelectElement).value;
    refreshTable();
  },
  "ef-q": (el) => {
    EF.q = (el as HTMLInputElement).value;
    refreshTable();
  },
  "mg-edit": (el) => openEditor(Number(el.dataset.id)),
  "mg-dup": (el) => openEditor(Number(el.dataset.id), true),
  "ask-publish-drafts": () => askPublishDrafts(),
  "publish-drafts": async () => {
    const notify = checked("pdn");
    try {
      const r = await api<{ published: number }>("/api/events/publish-drafts", { body: { notify } });
      closeSheet();
      for (const e of E.byId.values()) if (e.status === "draft") put({ ...e, status: "published" });
      resetManage();
      render();
      toast(`${r.published} event${r.published === 1 ? "" : "s"} published. ${notify ? "Matching ambassadors will be notified." : "No notification sent."}`);
    } catch (e) {
      toast((e as Error).message);
    }
  },
  "export-events": () => exportEvents(),
  "tpl-count": () => ($("#tnc")!.textContent = `${val("tn").length}/30`),
  "tpl-type": () => setDot("tt", val("tt")),
  "open-tpl": (el) => openTpl(Number(el.dataset.id)),
  "save-tpl": async (el) => {
    const id = Number(el.dataset.id);
    const body = {
      name: val("tn").trim(),
      type: val("tt"),
      start: tget("tst"),
      duration: Number(val("tdur")),
      locationId: val("tl") ? Number(val("tl")) : null,
      pa: Number(val("tpa")),
      sa: Number(val("tsa")),
      paShadow: checked("tpsh"),
      saShadow: checked("tssh"),
    };
    if (!body.name) return showErr(new Error("Give the template a name."));
    if (!body.type) return showErr(new Error("Choose a type."));
    try {
      const t = await api<TemplateView>(id ? `/api/events/templates/${id}` : "/api/events/templates", { method: id ? "PATCH" : "POST", body });
      E.templates = [...(E.templates || []).filter((x) => x.id !== t.id), t];
      closeSheet();
      render();
      toast("Template saved");
    } catch (e) {
      showErr(e);
    }
  },
  "ask-del-tpl": (el) => {
    $("#tdel")!.innerHTML = `<span class="row wrap" style="gap:6px"><span class="small"><b>Delete?</b> Events made from it are unchanged.</span><button class="btn danger sm" data-act="del-tpl" data-id="${el.dataset.id}">Yes, Delete</button></span>`;
  },
  "del-tpl": async (el) => {
    const id = Number(el.dataset.id);
    try {
      await api(`/api/events/templates/${id}`, { method: "DELETE" });
      E.templates = (E.templates || []).filter((x) => x.id !== id);
      closeSheet();
      render();
      toast("Template deleted. Events made from it are unchanged.");
    } catch (e) {
      toast((e as Error).message);
    }
  },
  "open-loc": (el) => openLoc(Number(el.dataset.id)),
  "save-loc": async (el) => {
    const id = Number(el.dataset.id);
    const body = { name: val("lname").trim(), address: val("laddr").trim(), directions: val("linfo").trim() };
    if (!body.name) return showErr(new Error("Give the location a name."));
    if (!body.address) return showErr(new Error("Add the address."));
    try {
      const l = await api<LocationView>(id ? `/api/events/locations/${id}` : "/api/events/locations", { method: id ? "PATCH" : "POST", body });
      E.locations = [...(E.locations || []).filter((x) => x.id !== l.id), l];
      // Events show the location by name, so refresh them next time they're loaded.
      if (id) resetEventCaches();
      closeSheet();
      render();
      toast(id ? "Saved. No invites were re-sent." : "Location added");
    } catch (e) {
      showErr(e);
    }
  },
  "ask-del-loc": (el) => {
    $("#ldel")!.innerHTML = `<span class="row wrap" style="gap:6px"><span class="small"><b>Delete?</b> Events that use it keep it, but it won't be offered for new events.</span><button class="btn danger sm" data-act="del-loc" data-id="${el.dataset.id}">Yes, Delete</button></span>`;
  },
  "del-loc": async (el) => {
    const id = Number(el.dataset.id);
    try {
      await api(`/api/events/locations/${id}`, { method: "DELETE" });
      E.locations = (E.locations || []).map((x) => (x.id === id ? { ...x, deleted: true } : x));
      closeSheet();
      render();
      toast("Location deleted. Events that use it keep it.");
    } catch (e) {
      toast((e as Error).message);
    }
  },
});

function resetEventCaches() {
  E.byId.clear();
  E.years.clear();
  resetManage();
}

